import { Helmet } from "react-helmet-async";

interface SEOProps {
  title?: string;
  description?: string;
  image?: string;
  url?: string;
  type?: "website" | "article" | "product";
  noIndex?: boolean;
  jsonLd?: object | object[];
}

const BASE_URL = "https://lineroid.com";
const DEFAULT_IMAGE = "https://lineroid.com/og-image.png";
const SITE_NAME = "Lineroid";
const TWITTER_HANDLE = "@lineroid";

export function SEO({
  title,
  description = "The global intelligence platform for humanoid robotics. Comprehensive data, neutral analysis, and strategic insights.",
  image = DEFAULT_IMAGE,
  url,
  type = "website",
  noIndex = false,
  jsonLd,
}: SEOProps) {
  const fullTitle = title ? `${title} | ${SITE_NAME}` : `${SITE_NAME} - Global Humanoid Robotics Intelligence`;
  const fullUrl = url ? `${BASE_URL}${url}` : BASE_URL;

  // Convert jsonLd to array if single object
  const jsonLdArray = jsonLd ? (Array.isArray(jsonLd) ? jsonLd : [jsonLd]) : [];

  return (
    <Helmet>
      {/* Primary Meta Tags */}
      <title>{fullTitle}</title>
      <meta name="title" content={fullTitle} />
      <meta name="description" content={description} />
      {noIndex && <meta name="robots" content="noindex, nofollow" />}
      
      {/* Open Graph / Facebook */}
      <meta property="og:type" content={type} />
      <meta property="og:url" content={fullUrl} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={image} />
      <meta property="og:site_name" content={SITE_NAME} />
      
      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:site" content={TWITTER_HANDLE} />
      <meta name="twitter:creator" content={TWITTER_HANDLE} />
      <meta name="twitter:url" content={fullUrl} />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={image} />
      <meta name="twitter:image:alt" content={title || "Lineroid - Humanoid Robotics Intelligence"} />
      
      {/* Canonical */}
      <link rel="canonical" href={fullUrl} />

      {/* JSON-LD Structured Data */}
      {jsonLdArray.map((data, index) => (
        <script key={index} type="application/ld+json">
          {JSON.stringify(data)}
        </script>
      ))}
    </Helmet>
  );
}

// Helper functions to generate JSON-LD schemas
export function generateOrganizationSchema() {
  return {
    "@context": "https://schema.org",
    "@type": "Organization",
    "name": "Lineroid",
    "url": BASE_URL,
    "description": "The global intelligence platform for humanoid robotics",
    "logo": `${BASE_URL}/favicon.png`,
    "sameAs": [
      "https://twitter.com/lineroid",
      "https://linkedin.com/company/lineroid",
      "https://youtube.com/@lineroid",
      "https://github.com/lineroid"
    ]
  };
}

export function generateWebsiteSchema() {
  return {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "name": "Lineroid",
    "url": BASE_URL,
    "description": "Comprehensive data, neutral analysis, and strategic insights for humanoid robotics",
    "potentialAction": {
      "@type": "SearchAction",
      "target": `${BASE_URL}/robots?search={search_term_string}`,
      "query-input": "required name=search_term_string"
    }
  };
}

export function generateRobotSchema(robot: {
  name: string;
  company: string;
  description: string;
  images: string[];
  priceRange?: string | null;
  status: string;
  year: number;
  country: string;
  specs?: { height?: string; weight?: string };
}) {
  return {
    "@context": "https://schema.org",
    "@type": "Product",
    "name": robot.name,
    "description": robot.description,
    "image": robot.images?.[0],
    "brand": {
      "@type": "Organization",
      "name": robot.company
    },
    "manufacturer": {
      "@type": "Organization",
      "name": robot.company
    },
    "category": "Humanoid Robot",
    "countryOfOrigin": robot.country,
    ...(robot.priceRange && {
      "offers": {
        "@type": "Offer",
        "price": robot.priceRange,
        "priceCurrency": "USD",
        "availability": robot.status === "production" 
          ? "https://schema.org/InStock" 
          : "https://schema.org/PreOrder"
      }
    }),
    "additionalProperty": [
      ...(robot.specs?.height ? [{
        "@type": "PropertyValue",
        "name": "Height",
        "value": robot.specs.height
      }] : []),
      ...(robot.specs?.weight ? [{
        "@type": "PropertyValue",
        "name": "Weight",
        "value": robot.specs.weight
      }] : []),
      {
        "@type": "PropertyValue",
        "name": "Status",
        "value": robot.status
      },
      {
        "@type": "PropertyValue",
        "name": "Year Announced",
        "value": robot.year
      }
    ]
  };
}

export function generateCompanySchema(company: {
  name: string;
  description: string;
  website?: string | null;
  country: string;
  foundedYear?: number | null;
  employees?: string | null;
  logoUrl?: string | null;
}) {
  return {
    "@context": "https://schema.org",
    "@type": "Organization",
    "name": company.name,
    "description": company.description,
    ...(company.logoUrl && { "logo": company.logoUrl }),
    ...(company.website && { "url": company.website }),
    "address": {
      "@type": "PostalAddress",
      "addressCountry": company.country
    },
    ...(company.foundedYear && { "foundingDate": company.foundedYear.toString() }),
    ...(company.employees && { "numberOfEmployees": company.employees }),
    "industry": "Humanoid Robotics"
  };
}

export function generateItemListSchema(items: { name: string; url: string; position: number }[]) {
  return {
    "@context": "https://schema.org",
    "@type": "ItemList",
    "itemListElement": items.map((item) => ({
      "@type": "ListItem",
      "position": item.position,
      "name": item.name,
      "url": item.url
    }))
  };
}

export function generateBreadcrumbSchema(items: { name: string; url: string }[]) {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": items.map((item, index) => ({
      "@type": "ListItem",
      "position": index + 1,
      "name": item.name,
      "item": `${BASE_URL}${item.url}`
    }))
  };
}

export function generateFAQSchema(faqs: { question: string; answer: string }[]) {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqs.map(faq => ({
      "@type": "Question",
      "name": faq.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.answer
      }
    }))
  };
}

export function generateArticleSchema(article: {
  title: string;
  description: string;
  url: string;
  image?: string;
  datePublished: string;
  dateModified?: string;
  author?: string;
}) {
  return {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": article.title,
    "description": article.description,
    "url": `${BASE_URL}${article.url}`,
    ...(article.image && { "image": article.image }),
    "datePublished": article.datePublished,
    "dateModified": article.dateModified || article.datePublished,
    "author": {
      "@type": "Organization",
      "name": article.author || "Lineroid"
    },
    "publisher": {
      "@type": "Organization",
      "name": "Lineroid",
      "url": BASE_URL
    }
  };
}
